import { cn } from "@/lib/utils";
import {
  getRobotFleetStatus,
  getRobotClientStatus,
  ROBOT_CLIENT_STATUS_VALUES,
  ROBOT_FLEET_STATUS_VALUES,
} from "@shared/robotStatusConfig";

interface StatusBadgeProps {
  status: string;
  type?: 'robot' | 'robotClient' | 'sav' | 'mission' | 'vehicle' | 'ticket' | 'ticket_priority' | 'prospect' | 'default';
  label?: string;
  className?: string;
}

export function StatusBadge({ status, type = 'default', label, className }: StatusBadgeProps) {
  const fleetStatus = type === "robot" ? getRobotFleetStatus(status) : null;
  const clientRobotStatus = type === "robotClient" ? getRobotClientStatus(status) : null;
  const getColors = () => {
    const s = (status ?? '').toLowerCase();
    
    if (type === 'robot') {
      if (s === ROBOT_FLEET_STATUS_VALUES.DISPONIBLE) return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
      if (s === ROBOT_FLEET_STATUS_VALUES.EN_MISSION) return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
      if (s === ROBOT_FLEET_STATUS_VALUES.EN_PANNE || s === ROBOT_FLEET_STATUS_VALUES.EN_REPARATION) return "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800";
    }

    if (type === 'robotClient') {
      if (s === ROBOT_CLIENT_STATUS_VALUES.ACTIF) return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
      if (s === ROBOT_CLIENT_STATUS_VALUES.EN_REPARATION) return "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800";
      if (s === ROBOT_CLIENT_STATUS_VALUES.HORS_SERVICE) return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700";
    }

    if (type === 'sav') {
      if (s === 'ouvert') return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800";
      if (s === 'en_cours') return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
      if (s === 'en_attente_pieces') return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800";
      if (s === 'resolu') return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
      if (s === 'cloture') return "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
    }

    if (type === 'vehicle') {
      if (s === 'disponible') return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
      if (s === 'en_mission') return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
    }

    if (type === 'mission') {
      if (s === 'confirme') return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800";
      if (s === 'en_cours') return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
      if (s === 'termine') return "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
      if (s === 'option') return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800";
      if (s === 'annule') return "bg-red-50 text-red-600 border-red-100 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900/50";
      if (s === 'realisee') return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
    }

    if (type === 'ticket') {
      if (s === 'nouveau') return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800";
      if (s === 'en_cours') return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
      if (s === 'en_attente_piece') return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800";
      if (s === 'resolu') return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
      if (s === 'ferme') return "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
    }

    if (type === 'ticket_priority') {
      if (s === 'urgent') return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800";
      if (s === 'eleve') return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800";
      if (s === 'moyen') return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800";
      if (s === 'faible') return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700";
    }

    if (type === 'prospect') {
      if (s === 'stand_by') return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700";
      if (s === 'date_proposee') return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800";
      if (s === 'demo_planifiee') return "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800";
      if (s === 'demo_realisee') return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800";
      if (s === 'gagne') return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
      if (s === 'perdu') return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800";
    }

    if (s === 'annule') return "bg-red-50 text-red-600 border-red-100 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900/50";
    if (s.includes('premium')) return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";

    return "bg-secondary text-secondary-foreground border-border";
  };

  return (
    <span className={cn(
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border",
      getColors(),
      className
    )}>
      {label || (
        fleetStatus
          ? (fleetStatus.known ? status.replace(/_/g, " ") : fleetStatus.label)
          : clientRobotStatus
            ? clientRobotStatus.label
            : (status ?? '').replace(/_/g, ' ')
      )}
    </span>
  );
}
